import { Badge } from '@/components/ui/badge'
import type { CustomerStatementInvoice } from '@/lib/services/customers/customers.types'
import {
  cn,
  formatCalendarDate,
  formatCurrency,
  normalizeCurrencyValue,
  type NumericInput,
} from '@/lib/utils'
import type { ReactNode } from 'react'

/** A name shown for an invoice: plain text, or the contact or user it names. */
type CustomerInvoiceDisplayValue =
  | string
  | { name?: string | null; display_name?: string | null; email?: string | null }
  | null

/** The invoice fields these helpers read: a statement's open invoice, or an order shown like one. */
export interface CustomerInvoiceDisplayFields {
  id?: number | string
  label?: CustomerInvoiceDisplayValue
  ordering_contact?: CustomerInvoiceDisplayValue
  recipient_name?: CustomerInvoiceDisplayValue
  due_date?: string | null
  days_past_due?: number | null
  remaining_total?: NumericInput
  financial_state?: { balance_due_total?: NumericInput } | null
}

export function getCustomerInvoiceDaysPastDue(invoice: CustomerInvoiceDisplayFields): number {
  return Math.max(0, Number(invoice?.days_past_due ?? 0))
}

export function getCustomerInvoiceBalance(invoice: CustomerInvoiceDisplayFields): number {
  if (invoice?.financial_state?.balance_due_total != null) {
    return normalizeCurrencyValue(invoice?.financial_state?.balance_due_total)
  }

  if (invoice?.remaining_total != null) {
    return normalizeCurrencyValue(invoice?.remaining_total)
  }

  // `amount` is the invoice total, not the balance due. Missing balance fields mean
  // this payload is not allowed to report an owed amount.
  return 0
}

export function getCustomerReceivableInvoices<
  TInvoice extends CustomerInvoiceDisplayFields = CustomerStatementInvoice,
>(receivables: { invoices: TInvoice[] } | null | undefined): TInvoice[] {
  return Array.isArray(receivables?.invoices) ? receivables.invoices : []
}

function getInvoiceDisplayString(value: CustomerInvoiceDisplayValue | undefined): string {
  if (typeof value === 'string') {
    return value
  }

  if (value && typeof value === 'object') {
    return value.name || value.display_name || value.email || ''
  }

  return value == null ? '' : String(value)
}

export function getCustomerInvoiceLabel(invoice: CustomerInvoiceDisplayFields): string {
  return getInvoiceDisplayString(invoice?.label) || `Order #${invoice?.id ?? ''}`
}

export function getCustomerInvoiceContactLabel(invoice: CustomerInvoiceDisplayFields): string {
  return (
    getInvoiceDisplayString(invoice?.ordering_contact) ||
    getInvoiceDisplayString(invoice?.recipient_name) ||
    'No order contact'
  )
}

export function formatCustomerInvoiceDueDate(
  invoice: CustomerInvoiceDisplayFields,
  fallback = 'No due date'
): string {
  return formatCalendarDate(invoice?.due_date, 'MMM D, YYYY', fallback)
}

export function formatCustomerInvoiceStatus(invoice: CustomerInvoiceDisplayFields): string {
  const daysPastDue = getCustomerInvoiceDaysPastDue(invoice)

  if (daysPastDue <= 0) {
    return 'Current'
  }

  return `${daysPastDue} ${daysPastDue === 1 ? 'day' : 'days'} late`
}

export function getCustomerInvoiceRowClass(
  invoice: CustomerInvoiceDisplayFields,
  className = ''
): string {
  const isPastDue = getCustomerInvoiceDaysPastDue(invoice) > 0

  return cn(
    'rounded-md border border-l-4 p-3 transition-colors',
    isPastDue
      ? 'border-status-warning-border border-l-warning bg-status-warning-background'
      : 'border-border border-l-transparent bg-muted/20',
    className
  )
}

interface CustomerInvoiceProps {
  invoice: CustomerInvoiceDisplayFields
  className?: string
}

export function CustomerInvoiceStatusPill({ invoice, className = '' }: CustomerInvoiceProps) {
  const isPastDue = getCustomerInvoiceDaysPastDue(invoice) > 0

  return (
    <Badge variant={isPastDue ? 'warning' : 'mutedSoft'} size="sm" className={className}>
      {formatCustomerInvoiceStatus(invoice)}
    </Badge>
  )
}

interface CustomerInvoiceFactProps {
  label: ReactNode
  children: ReactNode
  className?: string
}

function CustomerInvoiceFact({ label, children, className = '' }: CustomerInvoiceFactProps) {
  return (
    <div className={cn('min-w-0', className)}>
      <span className="block text-sm font-medium leading-none text-muted-foreground">{label}</span>
      <span className="mt-1 block whitespace-nowrap text-sm font-medium leading-5 tabular-nums">
        {children}
      </span>
    </div>
  )
}

interface CustomerInvoiceDueDateCellProps extends CustomerInvoiceProps {
  dueFallback?: string
}

export function CustomerInvoiceDueDateCell({
  invoice,
  dueFallback = 'No due date',
  className = '',
}: CustomerInvoiceDueDateCellProps) {
  return (
    <CustomerInvoiceFact label="Due" className={className}>
      {formatCustomerInvoiceDueDate(invoice, dueFallback)}
    </CustomerInvoiceFact>
  )
}

interface CustomerInvoiceBalanceProps extends CustomerInvoiceProps {
  /** The balance to show instead of the invoice's own. */
  balance?: number | null
}

export function CustomerInvoiceBalanceCell({
  invoice,
  balance = null,
  className = '',
}: CustomerInvoiceBalanceProps) {
  const displayBalance = balance === null ? getCustomerInvoiceBalance(invoice) : balance

  return (
    <CustomerInvoiceFact label="Balance" className={className}>
      <span className="font-semibold">{formatCurrency(displayBalance)}</span>
    </CustomerInvoiceFact>
  )
}

export function CustomerInvoiceDueBalance({
  invoice,
  balance = null,
  dueFallback = 'No due date',
  className = '',
}: CustomerInvoiceBalanceProps & CustomerInvoiceDueDateCellProps) {
  const displayBalance = balance === null ? getCustomerInvoiceBalance(invoice) : balance

  return (
    <div className={cn('grid w-fit grid-cols-[max-content_max-content] gap-x-5', className)}>
      <CustomerInvoiceDueDateCell invoice={invoice} dueFallback={dueFallback} />
      <CustomerInvoiceBalanceCell balance={displayBalance} invoice={invoice} />
    </div>
  )
}
